/**
 * Copyright (c) 2025 Tj L Collins, DaddySavesTheWorld, and Mythbound Adventure Studio
 * All Rights Reserved
 *
 * @fileoverview VoicePanel — Floating overlay that composes all voice UI components
 * @version 1.0.0
 * @since v0.0.0897
 *
 * Purpose: Unified floating voice panel for Mythbound(TM) Adventure Studio sessions.
 * Composes VoiceControls, VoiceStatus, and VoiceBridgeInfo into a single overlay widget
 * that can be minimized/expanded during gameplay.
 *
 * Created: 2026-02-23 — Section 5 Voice Bridge UI Wiring (Frontend Core PRD)
 *
 * FEATURES:
 * - Default: Floating overlay anchored bottom-right
 * - Minimized state: Compact icon in sidebar with participant count badge +
 *   connection status dot (green=connected, yellow=connecting, red=error, gray=disconnected)
 * - Expanded state: Full controls — Join/Leave, Mute, PTT/VA toggle, volume slider,
 *   participant list with speaking indicators, bridge info
 * - localStorage persistence for minimized/expanded preference
 *
 * DEPENDENCIES:
 * - VoiceControls: Join/Leave, Mute, PTT/VA, volume, mode selection
 * - VoiceStatus: Connection state, speaking indicators, participant list
 * - VoiceBridgeInfo: Discord bridge awareness (inline variant)
 * - audioSettings: Connection state constants
 * - voiceMessages (i18n): Localization-ready strings
 *
 * Pipeline: User clicks VoicePanel → VoiceControls dispatches WebSocket events →
 *   Backend processes → WebSocket pushes updates → VoiceStatus reflects state
 */

import React, { useState, useEffect, useCallback } from 'react';
import { VoiceControls, type VoiceMode, type VoiceInputMode } from './VoiceControls';
import { VoiceStatus } from './VoiceStatus';
import { VoiceBridgeInfo, type BridgeStatus } from './VoiceBridgeInfo';
import { PlayerMicToggles } from './PlayerMicToggles'; // Added: 2026-04-16 (PRD_PlayerLevelMicControls_v1.md § Phase A)
import {
  VOICE_CONNECTION_STATE,
  type VoiceConnectionState,
} from '../../config/audioSettings';
import { voicePanel, formatMessage } from '../../i18n/voiceMessages';
import { useCampaign } from '../../context/CampaignContext'; // Added: 2026-04-16 (PRD_PlayerLevelMicControls_v1.md § Phase A)
import { audioProcessor } from '../../integration/AudioProcessor'; // Added: 2026-04-16 (PRD_PlayerLevelMicControls_v1.md § Phase A)
import { getSessionErrorMessage } from '../../services/api/SessionService';

// =============================================================================
// TYPES
// =============================================================================

export interface VoicePanelProps {
  /** Session ID for the active game session */
  sessionId: string;
  /** Authenticated user / participant ID */
  participantId: string;
  /** Whether the current user is the GM (enables extra controls) */
  isGM?: boolean;
  /** Canonical persisted session status. */
  sessionStatus?: 'scheduled' | 'active' | 'paused' | 'ended' | null;
  /** Shared session-level mode flag returned by the persisted state endpoint. */
  masterModeEnabled?: boolean;
  /** Sets the shared session-level mode flag through the authenticated API. */
  onMasterModeChange?: (enabled: boolean) => Promise<void>;
  /** Optional map of participantId -> character name for display */
  characterNameMap?: Map<string, string>;
  /** Discord bridge status (forwarded to VoiceBridgeInfo) */
  bridgeStatus?: BridgeStatus;
  /** Whether Discord relay is enabled */
  bridgeRelayEnabled?: boolean;
  /** Number of web listeners (forwarded to VoiceBridgeInfo) */
  webListeners?: number;
  /** Number of Discord listeners (forwarded to VoiceBridgeInfo) */
  discordListeners?: number;
  /** Start minimized (default: false) */
  initialMinimized?: boolean;
  /** Optional CSS class */
  className?: string;
}

// localStorage key for persisting minimized state
const LS_KEY_MINIMIZED = 'mythbound_voice_panel_minimized';

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * VoicePanel — Floating overlay that unifies voice UI for Mythbound(TM) sessions.
 *
 * Renders as a fixed-position panel in the bottom-right corner. Can be collapsed
 * to a compact badge showing participant count and connection status, or expanded
 * to the full voice control surface.
 *
 * Integration:
 * - Drop into SessionView.tsx (or any session-scoped page)
 * - Requires sessionId + participantId at minimum
 * - All internal state (connection, mute, volume, PTT) managed by child components
 */
export const VoicePanel: React.FC<VoicePanelProps> = ({
  sessionId,
  participantId,
  isGM = false,
  sessionStatus = null,
  masterModeEnabled = false,
  onMasterModeChange,
  characterNameMap,
  bridgeStatus = 'relay_pending',
  bridgeRelayEnabled = false,
  webListeners = 0,
  discordListeners = 0,
  initialMinimized,
  className = '',
}) => {
  // ---------------------------------------------------------------------------
  // PANEL STATE
  // ---------------------------------------------------------------------------

  const [isMinimized, setIsMinimized] = useState<boolean>(() => {
    if (initialMinimized !== undefined) return initialMinimized;
    try {
      const saved = localStorage.getItem(LS_KEY_MINIMIZED);
      return saved === 'true';
    } catch {
      return false;
    }
  });

  // Voice state tracked at panel level for minimized badge display
  const [connectionState, setConnectionState] = useState<VoiceConnectionState>(
    VOICE_CONNECTION_STATE.DISCONNECTED
  );
  const [isMuted, setIsMuted] = useState(false);
  const [voiceMode, setVoiceMode] = useState<VoiceMode>('transcribe_all');
  const [participantCount, setParticipantCount] = useState(0);
  const [isChangingMasterMode, setIsChangingMasterMode] = useState(false);
  const [masterModeError, setMasterModeError] = useState<string | null>(null);

  // Added: 2026-04-16 (PRD_PlayerLevelMicControls_v1.md § Phase A)
  // Campaign context — participants list + multi_speaker_mode for player_level mic controls.
  // multi_speaker_mode lives in Campaign.campaign_settings (generic Record<string, any>),
  // stored by VoiceSettings.tsx when the GM configures the campaign voice policy.
  const { currentCampaign, participants } = useCampaign();
  const multiSpeakerMode = currentCampaign?.campaign_settings?.multi_speaker_mode as string | undefined;

  // Added: 2026-04-16 (PRD_PlayerLevelMicControls_v1.md § Phase A)
  // Mic-enabled state for player_level mode. Keyed by user_id; persisted per sessionId so
  // toggling survives panel collapse/expand within the same session.
  // Missing key = true (default enabled). Restored from localStorage on mount.
  const MIC_STATES_LS_KEY = `mythbound_mic_states_${sessionId}`;
  const [micStates, setMicStates] = useState<Record<string, boolean>>(() => {
    try {
      const stored = localStorage.getItem(`mythbound_mic_states_${sessionId}`);
      return stored ? (JSON.parse(stored) as Record<string, boolean>) : {};
    } catch {
      return {};
    }
  });

  // Persist micStates to localStorage whenever they change
  // Added: 2026-04-16 (PRD_PlayerLevelMicControls_v1.md § Phase A)
  useEffect(() => {
    try {
      localStorage.setItem(MIC_STATES_LS_KEY, JSON.stringify(micStates));
    } catch {
      // localStorage unavailable — state remains in-memory only
    }
  }, [micStates, MIC_STATES_LS_KEY]);

  // Persist minimized preference
  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY_MINIMIZED, String(isMinimized));
    } catch {
      // localStorage unavailable
    }
  }, [isMinimized]);

  // Derive participant count from web + discord listeners
  useEffect(() => {
    setParticipantCount(webListeners + (bridgeRelayEnabled ? discordListeners : 0));
  }, [webListeners, discordListeners, bridgeRelayEnabled]);

  // ---------------------------------------------------------------------------
  // CALLBACKS from child components
  // ---------------------------------------------------------------------------

  const handleConnectionStateChange = useCallback((state: VoiceConnectionState) => {
    setConnectionState(state);
  }, []);

  const handleMuteChange = useCallback((muted: boolean) => {
    setIsMuted(muted);
  }, []);

  const handleModeChange = useCallback((mode: VoiceMode) => {
    setVoiceMode(mode);
  }, []);

  const toggleMinimize = useCallback(() => {
    setIsMinimized((prev) => !prev);
  }, []);

  /**
   * Handles per-player mic toggle in player_level multi-speaker mode.
   * When the toggled userId matches participantId (own row), gates audio frames
   * via AudioProcessor.setMicEnabled(). Other participants' rows update visual
   * state only — cross-user audio enforcement is out of scope (display-only).
   * Added: 2026-04-16 (PRD_PlayerLevelMicControls_v1.md § Phase A)
   */
  const handleMicToggle = useCallback((userId: string, enabled: boolean) => {
    setMicStates((prev) => ({ ...prev, [userId]: enabled }));
    if (userId === participantId) {
      // Gate own audio frames — capture pipeline stays running (instant re-enable)
      audioProcessor.setMicEnabled(enabled);
    }
  }, [participantId]);

  // ---------------------------------------------------------------------------
  // CONNECTION STATUS DOT COLOR
  // green=connected, yellow=connecting, red=error, gray=disconnected
  // ---------------------------------------------------------------------------
  const getStatusDotColor = (): string => {
    switch (connectionState) {
      case VOICE_CONNECTION_STATE.CONNECTED:
        return '#22c55e'; // green
      case VOICE_CONNECTION_STATE.CONNECTING:
      case VOICE_CONNECTION_STATE.RECONNECTING:
        return '#eab308'; // yellow
      case VOICE_CONNECTION_STATE.ERROR:
        return '#ef4444'; // red
      default:
        return '#6b7280'; // gray
    }
  };

  const getStatusDotLabel = (): string => {
    switch (connectionState) {
      case VOICE_CONNECTION_STATE.CONNECTED:
        return voicePanel.connectionDot.connected;
      case VOICE_CONNECTION_STATE.CONNECTING:
      case VOICE_CONNECTION_STATE.RECONNECTING:
        return voicePanel.connectionDot.connecting;
      case VOICE_CONNECTION_STATE.ERROR:
        return voicePanel.connectionDot.error;
      default:
        return voicePanel.connectionDot.disconnected;
    }
  };

  const showMasterModeToggle = Boolean(onMasterModeChange) &&
    (masterModeEnabled || sessionStatus === 'paused');
  const masterModeButton = showMasterModeToggle ? (
    <button
      type="button"
      onClick={async () => {
        if (!onMasterModeChange || isChangingMasterMode) return;
        setMasterModeError(null);
        setIsChangingMasterMode(true);
        try {
          await onMasterModeChange(!masterModeEnabled);
        } catch (error) {
          setMasterModeError(getSessionErrorMessage(error, 'Could not update session mode.'));
        } finally {
          setIsChangingMasterMode(false);
        }
      }}
      style={panelStyles.modeButton}
      aria-pressed={masterModeEnabled}
      aria-label={masterModeEnabled ? 'Myth Mode' : 'Master Mode'}
      title={masterModeEnabled ? 'Disable Master Mode' : 'Enable Master Mode'}
      disabled={isChangingMasterMode}
    >
      {isChangingMasterMode ? 'Updating…' : masterModeEnabled ? 'Myth Mode' : 'Master Mode'}
    </button>
  ) : null;

  // =========================================================================
  // MINIMIZED VIEW
  // Compact badge: connection dot + mic icon + participant count
  // =========================================================================
  if (isMinimized) {
    return (
      <div
        className={`voice-panel minimized ${className}`}
        style={panelStyles.minimizedContainer}
        role="region"
        aria-label={voicePanel.region.ariaLabel}
      >
        <button
          onClick={toggleMinimize}
          style={panelStyles.minimizedButton}
          aria-label={formatMessage(voicePanel.minimized.ariaLabel, {
            count: participantCount,
          })}
          title={voicePanel.minimized.expand}
        >
          {/* Connection status dot */}
          <span
            style={{
              ...panelStyles.statusDot,
              backgroundColor: getStatusDotColor(),
            }}
            aria-label={getStatusDotLabel()}
          />
          {/* Mic / muted icon */}
          <span style={panelStyles.minimizedIcon}>
            {isMuted ? '\uD83D\uDD07' : '\uD83C\uDF99\uFE0F'}
          </span>
          {/* Participant count badge */}
          {participantCount > 0 && (
            <span style={panelStyles.participantBadge}>
              {participantCount}
            </span>
          )}
        </button>
        {masterModeButton}
      </div>
    );
  }

  // =========================================================================
  // EXPANDED VIEW
  // Full voice controls panel
  // =========================================================================
  return (
    <div
      className={`voice-panel expanded ${className}`}
      style={panelStyles.expandedContainer}
      role="region"
      aria-label={voicePanel.region.ariaLabel}
    >
      {/* Panel Header */}
      <div style={panelStyles.header}>
        <span style={panelStyles.headerTitle}>
          <span
            style={{
              ...panelStyles.headerDot,
              backgroundColor: getStatusDotColor(),
            }}
            aria-hidden="true"
          />
          {voicePanel.title}
        </span>
        <div style={panelStyles.headerControls}>
          {/* Minimize button */}
          <button
            onClick={toggleMinimize}
            style={panelStyles.controlBtn}
            aria-label={voicePanel.expanded.minimize.ariaLabel}
            title={voicePanel.expanded.minimize.title}
          >
            &minus;
          </button>
        </div>
      </div>

      {masterModeButton && (
        <div style={panelStyles.modeSection} aria-live="polite">
          {masterModeButton}
          {masterModeError && <div role="alert" style={panelStyles.modeError}>{masterModeError}</div>}
        </div>
      )}

      {/* Voice Controls: Join/Leave, Mute, PTT/VA, Mode, Volume */}
      <div style={panelStyles.section}>
        <VoiceControls
          sessionId={sessionId}
          participantId={participantId}
          onConnectionStateChange={handleConnectionStateChange}
          onModeChange={handleModeChange}
          onMuteChange={handleMuteChange}
          showVolumeSlider={true}
          showBufferPreset={false}
          compact={false}
        />
      </div>

      {/* Per-player mic toggles — only rendered when multi_speaker_mode='player_level' */}
      {/* Added: 2026-04-16 (PRD_PlayerLevelMicControls_v1.md § Phase A) */}
      {/* Allows each player to toggle their own mic on/off during a session. */}
      {/* Audio gating is enforced by AudioProcessor.setMicEnabled() for own row. */}
      {multiSpeakerMode === 'player_level' && (
        <div style={{ ...panelStyles.section, paddingTop: '10px', paddingBottom: '10px' }}>
          <PlayerMicToggles
            participants={participants}
            micStates={micStates}
            onToggle={handleMicToggle}
            currentUserId={participantId}
          />
        </div>
      )}

      {/* Voice Status: Connection, speaking indicators, participant list */}
      <div style={panelStyles.section}>
        <VoiceStatus
          connectionState={connectionState}
          voiceMode={voiceMode}
          isMuted={isMuted}
          sessionId={sessionId}
          showParticipants={true}
          maxParticipants={6}
          characterNameMap={characterNameMap}
        />
      </div>

      {/* Discord Bridge Info (inline variant) */}
      <div style={panelStyles.bridgeSection}>
        <VoiceBridgeInfo
          variant="inline"
          bridgeStatus={bridgeStatus}
          bridgeRelayEnabled={bridgeRelayEnabled}
          webListeners={webListeners}
          discordListeners={discordListeners}
          isMuted={isMuted}
        />
      </div>
    </div>
  );
};

// =============================================================================
// STYLES
// Updated 2026-02-23: Inline styles for encapsulation; can be moved to CSS module
// =============================================================================

const panelStyles: Record<string, React.CSSProperties> = {
  // ---------------------------------------------------------------------------
  // MINIMIZED
  // ---------------------------------------------------------------------------
  minimizedContainer: {
    position: 'fixed',
    bottom: '20px',
    right: '20px',
    zIndex: 1000,
  },
  minimizedButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    backgroundColor: 'rgba(30, 30, 30, 0.95)',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    borderRadius: '24px',
    cursor: 'pointer',
    color: '#fff',
    backdropFilter: 'blur(12px)',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.4)',
    transition: 'all 0.2s ease',
    fontFamily: '"Cinzel", serif',
  },
  statusDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  minimizedIcon: {
    fontSize: '16px',
  },
  participantBadge: {
    fontSize: '11px',
    fontWeight: 'bold',
    backgroundColor: '#3b82f6',
    color: '#fff',
    padding: '2px 7px',
    borderRadius: '10px',
    minWidth: '20px',
    textAlign: 'center',
  },

  // ---------------------------------------------------------------------------
  // EXPANDED
  // ---------------------------------------------------------------------------
  expandedContainer: {
    position: 'fixed',
    bottom: '20px',
    right: '20px',
    width: '340px',
    maxHeight: 'calc(100vh - 120px)',
    overflowY: 'auto',
    backgroundColor: 'rgba(20, 20, 24, 0.97)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '14px',
    boxShadow: '0 8px 40px rgba(0, 0, 0, 0.5)',
    zIndex: 1000,
    fontFamily: '"Cinzel", serif',
    backdropFilter: 'blur(16px)',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#d4af37', // Mythbound gold
    letterSpacing: '0.03em',
  },
  headerDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    display: 'inline-block',
  },
  headerControls: {
    display: 'flex',
    gap: '4px',
  },
  controlBtn: {
    width: '26px',
    height: '26px',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
    color: 'rgba(255, 255, 255, 0.6)',
    cursor: 'pointer',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.15s',
  },
  modeSection: {
    padding: '8px 12px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  modeButton: {
    border: '1px solid rgba(212, 175, 55, 0.65)',
    borderRadius: '8px',
    padding: '8px 12px',
    backgroundColor: 'rgba(212, 175, 55, 0.14)',
    color: '#f3d77b',
    cursor: 'pointer',
    fontFamily: '"Cinzel", serif',
    fontWeight: 600,
  },
  modeError: {
    color: '#fca5a5',
    fontSize: '12px',
  },
  section: {
    padding: '8px 12px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
  },
  bridgeSection: {
    padding: '8px 12px',
  },
};

export default VoicePanel;
